import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  LOCALE_ID,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MemberAccount } from '../../../../models/transactions/member-account';
import { MonthlyPensionAnnuityTypeLabelPipe } from '../../../records/components/members/ip-member-record/shared/pipes/monthly-pension-annuity-type-label.pipe';
import { StatusPillComponent } from '../../../../ui/pills/status-pill.component';
import { MemberAccountStatusIdEnum } from '../../../../models/transactions/enum/member-account-status-id.enum';
import { MemberActiveStatusPillPipe } from '../../pipes/member-active-status-pill.pipe';
import { TextButtonComponent } from '../../../../ui/buttons/text-button/text-button.component';
import { ButtonTypeEnum } from '../../../../ui/buttons/button/button-type.enum';
import { LeadingZeroPipe } from '../../pipes/leading-zero.pipe';
import { PortalService } from '../../../../services/portal/portal.service';
import { SplitStatusPillPipe } from '../../../../pipes/split-status-pill.pipe';
import { struckAccountDescription } from '../../../../features/change-account-status/utils/account-status-date-utils';
import { IpAccountStatusMenuComponent } from '../../../../features/change-account-status/ip-account-status-menu/ip-account-status-menu.component';

@Component({
  selector: 'app-monthly-pension-account-container',
  standalone: true,
  imports: [
    CommonModule,
    MonthlyPensionAnnuityTypeLabelPipe,
    StatusPillComponent,
    MemberActiveStatusPillPipe,
    TextButtonComponent,
    LeadingZeroPipe,
    SplitStatusPillPipe,
    IpAccountStatusMenuComponent
  ],
  templateUrl: './monthly-pension-account-container.component.html',
  styleUrls: ['./monthly-pension-account-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MonthlyPensionAccountContainerComponent implements OnChanges {
  @Input({ required: true }) monthlyPensionAccount!: MemberAccount;
  @Input() showStatusMenu = false;
  @Output() viewAccount = new EventEmitter<number>();
  @Output() viewSubmission = new EventEmitter<void>();
  @Output() statusSelected = new EventEmitter<MemberAccountStatusIdEnum>();

  private portalService = inject(PortalService);
  private locale = inject(LOCALE_ID);

  public isMemberPortal$ = this.portalService.isMember$;

  public descriptionText: string = '';
  public effectiveDateInFuture: boolean = false;
  public isStruck: boolean = false;
  public hasSplitLegs: boolean = false;
  public monthlyAmount: number | null = null;
  public iconButtonSrc: string = 'assets/icons/primary/Chevron Right.svg';

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.monthlyPensionAccount) {
      const details = this.monthlyPensionAccount.monthlyPensionAccountDetails;
      this.hasSplitLegs = details?.isSplit ?? false;
      this.isStruck = this.monthlyPensionAccount.isStruck();
      this.monthlyAmount = this.isStruck ? 0 : details?.ownerCurrentMonthlyAmount ?? null;
      this.setDescriptionText();
    }
  }

  private setDescriptionText() {
    this.descriptionText = '';
    this.effectiveDateInFuture = false;
    this.iconButtonSrc = 'assets/icons/primary/Chevron Right.svg';
    if (this.isStruck) {
      this.descriptionText = struckAccountDescription(this.monthlyPensionAccount, this.locale) ?? '';
      this.iconButtonSrc = 'assets/icons/grey/Chevron Right.svg';
      return;
    }
    const details = this.monthlyPensionAccount.monthlyPensionAccountDetails;
    if (!details) {
      return;
    }
    if (details.isEffectiveDateInFuture()) {
      const date = new Date(details.effectiveDate);
      const month = date.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });
      const year = date.getUTCFullYear();
      this.descriptionText = $localize`Starting ${month} ${year}`;
      this.effectiveDateInFuture = true;
      this.iconButtonSrc = 'assets/icons/grey/Chevron Right.svg';
    }
  }

  public goToAccount(accountId: number): void {
    this.viewAccount.emit(accountId);
  }

  // TODO
  public goToSubmission(): void {
    this.viewSubmission.emit();
  }

  protected readonly MemberAccountStatusIdEnum = MemberAccountStatusIdEnum;
  protected readonly ButtonTypeEnum = ButtonTypeEnum;
}
